import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { createPageUrl } from "@/shared/utils";
import { auth } from "@/shared/api/graphqlClient";
import { LanguageProvider, useLanguage } from "@/shared/components/shared/LanguageContext";
import LanguageSwitcher from "@/shared/components/shared/LanguageSwitcher";
import {
  LayoutDashboard,
  Building2,
  Receipt,
  ArrowDownToLine,
  ArrowUpFromLine,
  Settings,
  LogOut,
  Menu,
  X,
  Zap,
  Bell,
  User,
  ChevronLeft,
  ChevronRight,
  Users,
  Server,
  Route,
  Webhook,
  FileText,
} from "lucide-react";
import { Button } from "@/shared/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/shared/components/ui/dropdown-menu";
import NotificationDropdown from "@/shared/components/shared/NotificationDropdown";

const getNavItems = (t) => [
  { name: t("dashboard"), icon: LayoutDashboard, page: "EngineDashboard" },
  { name: t("transactions"), icon: Receipt, page: "EngineTransactions" },
  { name: t("deposits"), icon: ArrowDownToLine, page: "EngineDeposits" },
  { name: t("withdrawals"), icon: ArrowUpFromLine, page: "EngineWithdrawals" },
  { name: t("merchants"), icon: Building2, page: "EngineMerchants" },
  { name: t("resellers"), icon: Users, page: "EngineResellers" },
  { name: t("providers"), icon: Server, page: "EngineProviders", viewOnly: true },
  { name: t("routing"), icon: Route, page: "EngineRouting" },
  { name: t("webhooks"), icon: Webhook, page: "EngineWebhooks" },
  { name: t("settlements"), icon: FileText, page: "EngineSettlements" },
  { name: t("settings"), icon: Settings, page: "EngineSettings" },
];

function EngineLayoutContent({ children, currentPage, resellerName = "Engine Partner", engineId = "ENG001" }) {
  const [collapsed, setCollapsed] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const navigate = useNavigate();
  const { t } = useLanguage();
  const navItems = getNavItems(t);

  const handleLogout = () => {
    auth.logout();
    window.location.href = '/EngineLogin';
  };
  

  return (
    <div className="min-h-screen bg-slate-50">
      <style>{`
                    .engine-sidebar {
                      background: linear-gradient(180deg, #0c4a6e 0%, #075985 100%);
                    }
                    .engine-nav-item {
                      transition: all 0.2s ease;
                    }
                    .engine-nav-item:hover {
                      transform: translateX(4px);
                    }
                    .engine-nav-item.active {
                      background: linear-gradient(135deg, #0ea5e9 0%, #0284c7 100%);
                      box-shadow: 0 4px 12px rgba(14, 165, 233, 0.4);
                    }
                    .engine-sidebar::-webkit-scrollbar,
                    .engine-sidebar nav::-webkit-scrollbar {
                      width: 6px;
                    }
                    .engine-sidebar::-webkit-scrollbar-track,
                    .engine-sidebar nav::-webkit-scrollbar-track {
                      background: transparent;
                    }
                    .engine-sidebar::-webkit-scrollbar-thumb,
                    .engine-sidebar nav::-webkit-scrollbar-thumb {
                      background: rgba(56, 189, 248, 0.3);
                      border-radius: 3px;
                    }
                    .engine-sidebar::-webkit-scrollbar-thumb:hover,
                    .engine-sidebar nav::-webkit-scrollbar-thumb:hover {
                      background: rgba(56, 189, 248, 0.5);
                    }
                  `}</style>

      {/* Mobile Header */}
      <div className="lg:hidden fixed top-0 left-0 right-0 h-16 bg-sky-900 z-50 flex items-center justify-between px-4">
        <div className="flex items-center gap-3">
          <Button
            variant="ghost"
            size="icon"
            className="text-white"
            onClick={() => setMobileOpen(!mobileOpen)}
          >
            {mobileOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </Button>
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-lg bg-sky-500 flex items-center justify-center">
              <Zap className="h-4 w-4 text-white" />
            </div>
            <span className="font-bold text-white">Luxkgate</span>
          </div>
        </div>
      </div>

      {/* Mobile Overlay */}
      {mobileOpen && (
        <div
          className="lg:hidden fixed inset-0 bg-black/50 z-40"
          onClick={() => setMobileOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside
        className={`engine-sidebar fixed top-0 left-0 h-screen z-50 transition-all duration-300 ease-in-out flex flex-col
          ${collapsed ? "w-20" : "w-64"}
          ${mobileOpen ? "translate-x-0" : "-translate-x-full lg:translate-x-0"}
        `}
      >
        {/* Logo */}
        <div className={`h-16 flex items-center border-b border-sky-700 flex-shrink-0 ${collapsed ? "justify-center px-2" : "px-6"}`}>
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-sky-400 to-sky-500 flex items-center justify-center shadow-lg shadow-sky-500/30">
              <Zap className="h-5 w-5 text-white" />
            </div>
            {!collapsed && (
              <div>
                <h1 className="font-bold text-white text-lg tracking-tight">Luxkgate</h1>
                <p className="text-[10px] text-sky-300 uppercase tracking-wider">{t("enginePanel")}</p>
              </div>
            )}
          </div>
        </div>

        {/* Navigation */}
        <nav className="p-4 space-y-1 flex-1 overflow-y-auto">
          {!collapsed && (
            <p className="text-[10px] uppercase tracking-wider text-sky-400 font-medium mb-3 px-3">
              {t("navigation")}
            </p>
          )}

          {navItems.map((item) => {
            const isActive = currentPage === item.page;
            return (
              <Link
                key={item.page}
                to={createPageUrl(item.page)}
                onClick={() => setMobileOpen(false)}
                className={`engine-nav-item flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium
                  ${isActive
                    ? "active text-white"
                    : "text-sky-200 hover:text-white hover:bg-sky-800/50"
                  }
                  ${collapsed ? "justify-center" : ""}
                `}
              >
                <item.icon className={`h-5 w-5 flex-shrink-0 ${isActive ? "text-white" : ""}`} />
                {!collapsed && (
                  <div className="flex items-center justify-between flex-1">
                    <span>{item.name}</span>
                    {item.viewOnly && (
                      <span className="text-[10px] bg-sky-500/50 px-1.5 py-0.5 rounded">{t("viewOnlyBadge")}</span>
                    )}
                  </div>
                )}
              </Link>
            );
          })}
        </nav>

        {/* Engine Info */}
        {!collapsed && (
          <div className="px-4 pb-16 mt-auto flex-shrink-0">
            <div className="bg-sky-800/50 rounded-xl p-4 border border-sky-700">
              <p className="text-xs text-sky-400 mb-1">{t("loggedInAs")}</p>
              <p className="text-white font-semibold text-sm truncate">{resellerName}</p>
              <p className="text-xs text-sky-400 mt-1">{t("engine")} ID: <span className="text-sky-200 font-mono">{engineId}</span></p>
            </div>
          </div>
        )}

        {/* Collapse Button */}
        <button
          onClick={() => setCollapsed(!collapsed)}
          className="hidden lg:flex absolute bottom-4 left-1/2 -translate-x-1/2 w-8 h-8 rounded-full bg-sky-700 items-center justify-center text-sky-300 hover:text-white hover:bg-sky-600 transition-colors"
        >
          {collapsed ? <ChevronRight className="h-4 w-4" /> : <ChevronLeft className="h-4 w-4" />}
        </button>
      </aside>

      {/* Main Content */}
      <div className={`transition-all duration-300 ${collapsed ? "lg:pl-20" : "lg:pl-64"}`}>
        {/* Top Bar */}
        <header className="sticky top-0 z-30 hidden lg:flex h-16 items-center justify-between px-6 bg-white/80 backdrop-blur-lg border-b border-slate-200">
          <div className="flex-1" />
          <div className="flex items-center gap-3">
            <LanguageSwitcher variant="ghost" />
            <NotificationDropdown accentColor="sky" />

            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" className="flex items-center gap-2 pl-2 pr-3">
                  <div className="w-8 h-8 rounded-full bg-gradient-to-br from-sky-500 to-sky-600 flex items-center justify-center">
                    <User className="h-4 w-4 text-white" />
                  </div>
                  <div className="text-left hidden sm:block">
                    <p className="text-sm font-medium">{resellerName}</p>
                    <p className="text-xs text-slate-500">{t("engine")}</p>
                  </div>
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                <div className="px-3 py-2 border-b">
                  <p className="text-sm font-medium">{resellerName}</p>
                  <p className="text-xs text-slate-500">{t("engine")} ID: {engineId}</p>
                </div>
                <DropdownMenuItem onClick={() => navigate(createPageUrl("EngineSettings"))}>
                  <Settings className="h-4 w-4 mr-2" />
                  {t("settings")}
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem className="text-red-600" onClick={handleLogout}>
                  <LogOut className="h-4 w-4 mr-2" />
                  {t("logout")}
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </header>

        {/* Page Content */}
        <main className="p-4 lg:p-6 pt-20 lg:pt-6 min-h-[calc(100vh-4rem)]">
          {children}
        </main>
      </div>
    </div>
  );
}

export default function EngineLayout(props) {
  return <EngineLayoutContent {...props} />;
}